import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Fund, FundSorting } from 'src/app/domain/fund-performance/dto/Fund';
import { FundAndAction, FundModalType } from '../modal-fund-update/modal-fund-update.component';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Organization } from 'src/app/domain/organization/dto/Organization';
import { FundService } from 'src/app/domain/organization/service/fund.service';
import { ToolTipSettings } from 'src/app/domain/shared/component/tooltip/tooltip.util';
import { ToolTipService } from 'src/app/tool-tip.service';
import { OrganizationAccesses, OrganizationsService } from '../../service/organizations.service';
import { FundOrderDTO } from '../../../dto/FundOrderDTO';
import { firstValueFrom } from 'rxjs';
import {
  FpOverviewDataShownGroups,
  GroupedFunds,
  ShowFundsTable,
} from "../../organization-overview/utils";
import { cloneDeep } from "lodash";
import { ExcelService } from 'src/app/domain/excel';
import { ExcelTemplateName } from 'src/app/domain/excel/interfaces';

@Component({
  selector: 'app-fund-table',
  templateUrl: './fund-table.component.html',
  styleUrls: ['./fund-table.component.scss'],
})
export class FundTableComponent implements OnChanges {

  @Input() funds: Fund[];
  @Input() selectedOrganization: Organization;
  @Input() isAdmin = false;
  @Input() orgAccess: OrganizationAccesses;

  @Output() showUpdateModal = new EventEmitter<FundAndAction>();
  @Output() isConfirmedUpdate: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() copyFund = new EventEmitter<Fund>();

  dateArray = new GroupedDateArray();
  toolTipSettings: ToolTipSettings = { show: false };
  fundNameId = 'fundName';
  managementCoId = 'managementCo';
  groupedFunds: GroupedFunds;
  shownGroups = FpOverviewDataShownGroups;
  show = new ShowFundsTable();

  /** Array with strings representing all fields of the form. */
  informationHeaders = [
    'Fund id',
    'Fund name',
    'Registration state',
    'Registration number',
    'Registration date',
    'Initial closing',
    'Final closing',
    'Investment period',
    'Fund\'s term',
    'Management company',
  ];

  protected readonly FundSorting = FundSorting;

  constructor(
    private fundService: FundService,
    private toolTipService: ToolTipService,
    private orgService: OrganizationsService,
    private excelService: ExcelService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.funds) {
      this.groupFunds();
    }
    if (changes.isAdmin || this.orgAccess.isOrganizationAdmin || this.orgAccess.isSuperAdmin) {
      this.redefineHeaders();
    }
  }

  update(fund: Fund): void {
    const fundToUpdate: FundAndAction = { fund, modalType: FundModalType.UPDATE };
    this.showUpdateModal.emit(fundToUpdate);
  }

  async updateSvp(fund: Fund): Promise<void> {
    const changedFund = cloneDeep(fund);
    changedFund.spv = !changedFund.spv;
    await this.fundService.update(changedFund, null, null);
    fund.spv = changedFund.spv;
    this.groupFunds();
    this.updateOrgFunds();
  }

  copy(fund: Fund): void {
    const fundToCopy: FundAndAction = { fund, modalType: FundModalType.COPY };
    this.showUpdateModal.emit(fundToCopy);
  }

  defineDatesInFunds(): void {
    const groups = Object.keys(this.dateArray);
    for (const group of groups) {
      const groupedFunds = this.groupedFunds[group] as Fund[];
      for (let i = 0; i < groupedFunds.length; i++) {
        const fund = groupedFunds[i];
        const dateArray: boolean[] = [];
        dateArray[0] = this.beforeToday(fund.registrationDate);
        dateArray[1] = this.beforeToday(fund.initialClosing);
        dateArray[2] = this.beforeToday(fund.finalClosing);
        dateArray[3] = this.beforeToday(fund.investmentPeriod);
        dateArray[4] = this.beforeToday(fund.fundTerm);
        this.dateArray[group][i] = dateArray;
      }
    }

  }

  beforeToday(d: string | Date): boolean {
    d = new Date(d);
    const currentDate = new Date();
    return d.getTime() < currentDate.getTime();
  }

  activateToolTip(id: string, text: string, maxLen: number): void {
    this.toolTipSettings = { ...this.toolTipService.activateToolTip(id, text, maxLen, this.toolTipSettings) };
  }

  deactivateToolTip(): void {
    this.toolTipSettings.show = false;
  }

  exportExcel(): void {
    const orgName = this.selectedOrganization.name;
    if (!orgName) {
      return;
    }
    this.excelService.exportByTemplate(this.funds, orgName + '-funds.xlsx', ExcelTemplateName.funds);
  }

  async drop(event: CdkDragDrop<string[]>, affectedFunds: Fund[]): Promise<void> {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const fundOrdersToUpdate: FundOrderDTO[] = [];
    moveItemInArray(affectedFunds, event.previousIndex, event.currentIndex);
    affectedFunds.forEach((fund, index) => {
      fund.order.inOrgOverviewTable = index;
      fundOrdersToUpdate.push(fund.order);
    });
    await this.updateOrders(fundOrdersToUpdate);
  }

  private redefineHeaders(): void {
    this.informationHeaders = [
      'Fund id',
      'Fund name',
      'Registration state',
      'Registration number',
      'Registration date',
      'Initial closing',
      'Final closing',
      'Investment period',
      'Fund\'s term',
      'Management company',
    ];
  }

  private groupFunds(): void {
    this.groupedFunds = new GroupedFunds(this.funds);
    this.defineDatesInFunds();
  }

  /**
   * Updates funds orders on the backend and {@link OrganizationsService#selected}
   */
  private async updateOrders(fundOrdersToUpdate: FundOrderDTO[]): Promise<void> {
    await firstValueFrom(this.fundService.updateFundOrders(fundOrdersToUpdate, this.selectedOrganization.uuid));
    this.groupFunds(); // is needed to sort all funds before providing them to the orgService
    this.updateOrgFunds();
  }

  private updateOrgFunds(): void {
    const org = this.orgService.selected$.value;
    org.funds = this.groupedFunds.all;
    this.orgService.setSelected(org);
  }
}

class GroupedDateArray {
  funds: boolean[][] = [];
  spvs: boolean[][] = [];
}
